import { Component, ElementRef, inject, computed, signal } from '@angular/core';
import { RecombinationsService } from '../services/recombinations.service';
import { Router } from '@angular/router';
import { UserService } from '../../shared/services/user.service';
import moment from 'moment-timezone';

@Component({
  standalone: false,
  selector: 'invd-update-banner',
  templateUrl: './update-banner.component.html',
  styleUrl: './update-banner.component.scss',
})
export class UpdateBannerComponent {
  private rcrService = inject(RecombinationsService);
  private router = inject(Router);
  private userService = inject(UserService);
  private el = inject(ElementRef);

  // Convert to signals for better reactivity
  protected bannerText = signal('');
  protected bannerHeader = signal('');
  protected shutoffDate = signal('');
  protected shutoffTime = signal('');
  protected showSuccess = signal(false);
  protected showError = signal(false);
  protected showBanner = signal(false);

  // Use computed for efficient reactivity
  protected isFormEmpty = computed(() => {
    const text = this.bannerText();
    return !text || text.trim() === '';
  });

  headerEditorModules = {
    toolbar: [['bold', 'italic', 'underline'], [{ size: [false, 'large', 'huge'] }]],
  };

  bodyEditorModules = {
    toolbar: [['bold', 'italic', 'underline', 'link'], [{ size: [false, 'large', 'huge'] }]],
  };
  private marqueeAnimations: Animation[] = [];

  protected onSubmit(): void {
    let textBody = this.bannerText();
    if (this.bannerHeader()) {
      textBody = `${this.bannerHeader()}–${this.bannerText()}`;
    }

    let shutoffTimestamp: string | null = null;
    if (this.shutoffDate()) {
      const date = this.shutoffDate();
      const time = this.shutoffTime().trim() || '00:00';

      const dateTimeString = `${date}T${time}:00`;
      const estDateTime = moment.tz(
        dateTimeString,
        'YYYY-MM-DDTHH:mm:ss',
        true,
        'America/New_York',
      );

      if (estDateTime.isValid()) {
        shutoffTimestamp = estDateTime.toISOString();
      }
    }

    const request = {
      bannerText: textBody,
      bannerPage: 'Dashboard',
      ...(shutoffTimestamp && { shutoffTime: shutoffTimestamp }),
    };

    this.rcrService.updateBannerMessage(request).subscribe({
      next: () => {
        this.updateLocalBannerState(textBody, shutoffTimestamp);
        this.showSuccess.set(true);
        this.bannerText.set('');
        this.bannerHeader.set('');
        this.shutoffDate.set('');
        this.shutoffTime.set('');
        setTimeout(() => {
          this.showSuccess.set(false);
          this.router.navigate(['/']);
        }, 2000);
      },
      error: () => {
        this.showError.set(true);
        this.bannerText.set('');
        this.bannerHeader.set('');
        this.shutoffDate.set('');
        this.shutoffTime.set('');
        setTimeout(() => {
          this.showError.set(false);
        }, 5000);
      },
    });
  }

  protected preview(): void {
    this.showBanner.set(false);
    setTimeout(() => {
      this.showBanner.set(true);
      this.updateMarqueeDurations();
    });
  }

  protected viewCurrentBanner(): void {
    if (!this.userService?.dynamicBannerMessage) {
      return;
    }

    this.userService.dynamicBannerMessage.forEach((item) => {
      if (
        item.pageComponent &&
        item.pageComponent.trim() &&
        (item.pageComponent.trim().includes('Dashboard') ||
          item.pageComponent.trim().includes('dashboard')) &&
        item.bannerMessage &&
        item.bannerMessage.length > 0
      ) {
        const firstMessage = item.bannerMessage[0];
        const messageParts = firstMessage.split('–');

        this.bannerHeader.set(this.decodeHtml(messageParts[0] || ''));
        this.bannerText.set(this.decodeHtml(messageParts[1] || ''));

        if (item.shutoffTime) {
          const shutoffDateTime = moment.tz(item.shutoffTime, 'America/New_York');
          if (shutoffDateTime.isValid()) {
            this.shutoffDate.set(shutoffDateTime.format('YYYY-MM-DD'));
            this.shutoffTime.set(shutoffDateTime.format('HH:mm'));
          }
        } else {
          this.shutoffDate.set('');
          this.shutoffTime.set('');
        }
      }
    });

    this.showBanner.set(true);
    this.updateMarqueeDurations();
  }

  protected clearDateTime(): void {
    this.shutoffDate.set('');
    this.shutoffTime.set('');
  }

  private updateLocalBannerState(textBody: string, shutoffTimestamp: string | null): void {
    if (!this.userService?.dynamicBannerMessage) {
      return;
    }

    const dashboardBannerIndex = this.userService.dynamicBannerMessage.findIndex(
      (item) =>
        item.pageComponent &&
        item.pageComponent.trim() &&
        (item.pageComponent.trim().includes('Dashboard') ||
          item.pageComponent.trim().includes('dashboard')),
    );

    const updatedBanner = {
      pageComponent: 'Dashboard',
      bannerMessage: [textBody],
      ...(shutoffTimestamp && { shutoffTime: shutoffTimestamp }),
    };

    if (dashboardBannerIndex !== -1) {
      this.userService.dynamicBannerMessage[dashboardBannerIndex] = updatedBanner;
    } else {
      this.userService.dynamicBannerMessage.push(updatedBanner);
    }
  }

  protected pauseMarquee(): void {
    this.marqueeAnimations.forEach((a) => a.pause());
  }

  protected resumeMarquee(): void {
    this.marqueeAnimations.forEach((a) => a.play());
  }

  private updateMarqueeDurations(): void {
    setTimeout(() => {
      const PIXELS_PER_SECOND = 60;
      const PAUSE_MS = 2000;
      this.marqueeAnimations.forEach((a) => a.cancel());
      this.marqueeAnimations = [];
      const containers = (this.el.nativeElement as HTMLElement).querySelectorAll<HTMLElement>(
        '.flipping-text, .flipping-text-header',
      );
      containers.forEach((container) => {
        const inner = container.querySelector<HTMLElement>('.marquee-inner');
        if (!inner) return;
        const overflow = Math.max(0, inner.scrollWidth - container.clientWidth);
        if (overflow <= 0) return;
        const travelMs = (overflow / PIXELS_PER_SECOND) * 1000;
        const totalMs = PAUSE_MS + travelMs + PAUSE_MS;
        const anim = inner.animate(
          [
            { transform: 'translateX(0)', offset: 0 },
            { transform: 'translateX(0)', offset: PAUSE_MS / totalMs },
            { transform: `translateX(-${overflow}px)`, offset: (PAUSE_MS + travelMs) / totalMs },
            { transform: `translateX(-${overflow}px)`, offset: 1 },
          ],
          { duration: totalMs, iterations: Infinity, easing: 'linear' },
        );
        this.marqueeAnimations.push(anim);
      });
    }, 100);
  }

  private decodeHtml(html: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }
}
